import { HttpErrorResponse } from '@angular/common/http';

/* Error preventivo que debe ser capturado */
export class ErrorFrontend extends Error {
  /** Status HTTP, cuando el error viene de una respuesta del servidor. */
  public readonly status?: number;
  /** Cuerpo de la respuesta de error, para leer los errores por campo de DRF. */
  public readonly cuerpo?: unknown;
  /** URL de la petición que falló, para agrupar en Sentry por endpoint. */
  public readonly url?: string;

  constructor(datos: any) {
    const cuerpo =
      datos instanceof HttpErrorResponse
        ? datos.error instanceof Blob
          ? blobToJson(datos.error)
          : datos.error
        : undefined;
    if (typeof datos === 'string') {
      super(datos);
    } else if (datos instanceof HttpErrorResponse) {
      const error = cuerpo;
      // El status 0 es la petición que nunca llegó (sin red, cortada, CORS). Su cuerpo es
      // un ProgressEvent que no aporta nada, así que se decide por el status.
      if (datos.status === 0) super('No hay conexión con el servidor');
      // Una página de error HTML (el 500 de Django, el 502 del proxy) no es un mensaje:
      // como título del issue en Sentry y en la alerta quedaba "<!doctype html>…"
      // (ATLAS-FRONTEND-7Z). El cuerpo se conserva entero en `cuerpo`.
      else if (esPaginaHtml(error)) super(`Error ${datos.status} del servidor`);
      else if (typeof error === 'string' && error.trim()) super(error);
      else if (error?.detail) super(error.detail);
      else if (error?.message) super(error.message);
      else if (error?.error) super(error.error);
      else if (error) super(JSON.stringify(error, null, 2));
      // Sin cuerpo el mensaje genérico no dice nada: al menos el status distingue
      // un 502 del proxy de un 500 del servidor cuando el error llega a Sentry.
      else super(`Error ${datos.status} del servidor`);
    } else if (typeof datos === 'object') {
      super(JSON.stringify(datos, null, 2));
    } else {
      super('Error desconocido');
    }
    if (datos instanceof HttpErrorResponse) {
      this.status = datos.status;
      this.cuerpo = cuerpo;
      this.url = datos.url ?? undefined;
    }
  }
}

/**
 * Extrae el mensaje de un error capturado, que en un `catch` es `unknown`.
 * @param {unknown} error - El error capturado.
 * @param {string} respaldo - El mensaje a usar si el error no trae uno.
 * @returns {string} - El mensaje del error, o el respaldo.
 * @author Juan Corral
 */
export function mensajeError(error: unknown, respaldo: string): string {
  return error instanceof Error && error.message ? error.message : respaldo;
}

/* Error de desarrollo (para alertar al desarrollador) */
export class ErrorDesarrollo extends Error {
  constructor(mensaje: string) {
    const mensajeFinal = '\n' + mensaje + '\nPor favor, contacte a soporte técnico.';
    super(mensajeFinal);
  }
}

/**
 * Indica si el cuerpo de una respuesta de error es una página HTML y no un mensaje.
 * @param {unknown} cuerpo - El cuerpo de la respuesta.
 * @returns {boolean} - Si es un documento o fragmento HTML.
 * @author Juan Corral
 */
function esPaginaHtml(cuerpo: unknown): boolean {
  return typeof cuerpo === 'string' && /^\s*<(!doctype\s+html|html|head|body)[\s>]/i.test(cuerpo);
}

/**
 * Convierte un Blob a JSON.
 * @param {Blob} blob - El Blob a convertir.
 * @returns {Object | string | null} - El JSON resultante, o el texto crudo si el cuerpo no era JSON.
 * @author Juan Corral
 */
function blobToJson(blob: Blob) {
  const xhr = new XMLHttpRequest();
  const url = URL.createObjectURL(blob);
  let text = '';
  try {
    xhr.overrideMimeType('text/plain; charset=x-user-defined');
    xhr.open('GET', url, false);
    xhr.send();
    const uint8 = Uint8Array.from(xhr.response, (c: any) => c.charCodeAt(0));
    text = new TextDecoder().decode(uint8);
  } finally {
    URL.revokeObjectURL(url);
  }

  // El cuerpo no siempre es JSON: un 502 del proxy, una respuesta cortada o vacía. Si se
  // dejara reventar el parse, el error que se pierde es el que se estaba construyendo.
  try {
    return JSON.parse(text);
  } catch {
    return text.trim() || null;
  }
}
